<template>
  <div class="app-container bg-cyber-dark min-h-screen">
    <router-view v-slot="{ Component, route }">
      <suspense>
        <template #default>
          <component :is="Component" :key="route.path" />
        </template>
        <template #fallback>
          <div style="padding: 50px; text-align: center; color: white;">加载中...</div>
        </template>
      </suspense>
    </router-view>
    
    <!-- 底部导航栏 -->
    <van-tabbar v-model="active" v-if="showTabbar" active-color="#00D9FF" inactive-color="#A0A0A0" class="bg-cyber-darker border-t border-gray-800">
      <van-tabbar-item to="/" icon="home-o">首页</van-tabbar-item>
      <van-tabbar-item to="/market" icon="apps-o">交易区</van-tabbar-item>
      <van-tabbar-item to="/publish" icon="plus" class="publish-btn">
        <template #icon="props">
          <div class="w-12 h-12 rounded-full bg-gradient-to-r from-neon-purple to-neon-blue flex items-center justify-center shadow-neon">
            <van-icon name="plus" size="24" color="#fff" />
          </div>
        </template>
      </van-tabbar-item>
      <van-tabbar-item to="/customer-service" icon="service-o">客服</van-tabbar-item>
      <van-tabbar-item to="/user" icon="user-o">我的</van-tabbar-item>
    </van-tabbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onErrorCaptured } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const userStore = useUserStore()

// 捕获异步组件加载错误
onErrorCaptured((err, instance, info) => {
  console.error('组件错误:', err)
  console.error('错误组件:', instance)
  console.error('错误信息:', info)
  return false
})

const active = ref(0)
const messageCount = ref(0)

// 应用启动时，如果有token则获取用户信息
onMounted(async () => {
  const token = localStorage.getItem('token')
  if (token && !userStore.userInfo) {
    try {
      await userStore.getUserInfo()
    } catch (error: any) {
      console.error('获取用户信息失败:', error)
      // 只有明确的401/403错误才清除token，其他错误（网络错误、服务器错误）保留token
      // 避免因为临时的网络问题导致用户被错误登出
      const statusCode = error?.response?.status
      const isAuthError = statusCode === 401 || statusCode === 403
      const isTokenInvalid = error?.message?.includes('401') || error?.message?.includes('未授权') || error?.message?.includes('登录已过期')

      if (isAuthError || isTokenInvalid) {
        console.log('Token已失效，清除登录状态')
        localStorage.removeItem('token')
        userStore.logout()
      } else {
        console.log('非认证错误，保留登录状态，用户可继续尝试访问')
      }
    }
  }
})

// 不显示底部导航的页面
const hideTabbarPages = ['/login', '/register', '/order/', '/chat/']

const showTabbar = computed(() => {
  return !hideTabbarPages.some(path => route.path.startsWith(path))
})

// 根据路由更新 active
watch(() => route.path, (path) => {
  if (path === '/') active.value = 0
  else if (path === '/market') active.value = 1
  else if (path === '/publish') active.value = 2
  else if (path === '/customer-service') active.value = 3
  else if (path === '/user') active.value = 4
}, { immediate: true })
</script>

<style>
.app-container {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

.publish-btn {
  position: relative;
  top: -20px;
  z-index: 100;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-thumb {
  background: #7B2CBF;
  border-radius: 2px;
}

::-webkit-scrollbar-track {
  background: #2a3150;
}

/* 霓虹发光效果 */
.neon-text {
  text-shadow: 0 0 5px #00D9FF, 0 0 10px #00D9FF, 0 0 20px #00D9FF;
}

.neon-border {
  box-shadow: 0 0 5px #00D9FF, 0 0 10px #00D9FF, inset 0 0 5px rgba(0, 217, 255, 0.1);
}
</style>
